import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiCall } from "services/api";
import { paramParsify } from "utils/queryParams";

export const useGetClientList = ({ enableAutoFetchByParams }) => {
  const location = useLocation();
  const queryParams = paramParsify(location?.search);

  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiCall({
        url: `/v1/company`,
        queryParams: {
          ...queryParams,
          limit: 10,
        },
        method: "GET",
      });
      setResponse(response);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...(enableAutoFetchByParams
      ? [String(queryParams?.page), String(queryParams?.name)]
      : []),
  ]);

  return { response, loading, error, refetch: fetchData };
};

export const useCreateClient = () => {
  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const mutate = useCallback(async (data) => {
    setLoading(true);
    try {
      const response = await apiCall({
        url: "/v1/company",
        method: "POST",
        data,
      });
      setResponse(response);
      return response;
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  return { mutate, response, loading, error };
};

export const useEditClient = ({ id }) => {
  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const mutate = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const response = await apiCall({
          url: `/v1/company/${id}`,
          method: "PUT",
          data,
        });
        setResponse(response);
        return response;
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    [id],
  );

  return { mutate, response, loading, error };
};

export const useCreateAccount = () => {
  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const mutate = useCallback(async (data) => {
    setLoading(true);
    try {
      const response = await apiCall({
        url: "/v1/user",
        method: "POST",
        data,
      });
      setResponse(response);
      return response;
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  return { mutate, response, loading, error };
};

export const useEditExternalUser = ({ id }) => {
  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const mutate = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const response = await apiCall({
          url: `/v1/user/management/${id}`,
          method: "PUT",
          data,
        });
        setResponse(response);
        return response;
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    [id],
  );

  return { mutate, response, loading, error };
};

export const useDeleteExternalUser = ({ id }) => {
  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const mutate = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiCall({
        url: `/v1/user/management/${id}`,
        method: "DELETE",
      });
      setResponse(response);
      return response;
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  return { mutate, response, loading, error };
};

export const useGetExternalUserDetail = ({ id } = {}) => {
  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiCall({
        url: `/v1/user/management/${id}`,
        method: "GET",
      });
      setResponse(response);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return { response, loading, error, refetch: fetchData };
};

export const useGetAccountList = ({ id, skip_pagination = false }) => {
  const location = useLocation();
  const queryParams = paramParsify(location?.search);

  const [response, setResponse] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiCall({
        url: `/v1/company/${id}/users`,
        queryParams: {
          page: queryParams?.["client-management-list-table_page"],
          name: queryParams?.["client-management-list-table_name"],
          skip_pagination,
        },
        method: "GET",
      });
      setResponse(response);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [id, queryParams, skip_pagination]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    String(id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    String(queryParams?.["client-management-list-table_page"]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    String(queryParams?.["client-management-list-table_name"]),
  ]);

  return { response, loading, error, refetch: fetchData };
};
